import * as React from 'react';
import {
    Alert, Autocomplete, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
    DialogTitle, Grid, IconButton, MenuItem, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip,
    Typography, Link as MuiLink,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Link as RouterLink } from 'react-router-dom';
import {
    AI_LABELS, deleteAiAgentMcpLink, deleteAiAgentSubagentLink, deleteAiAgentTaskLink, getAiAgentLinks, getAiAgents,
    getAiAgentTools, getAiMcpServers, getAiMcpServerTools, getAiRun, getAiTasks, putAiAgentMcpLink,
    putAiAgentSubagentLink, putAiAgentTaskLink, startAiAgentRun,
} from '../../Api';
import {
    AiAgentLinksDto, AiAgentMcpLinkDto, AiAgentMessageDto, AiAgentToolsDto, AiBoundArgs, AiJsonObject, AiMcpServerDto,
    AiMcpToolDto, AiRunDetailDto,
} from '../../models/dtos/AiDto';
import { ConfirmDialog } from '../betupFeed/BetupFeedShared';
import {
    AiActionError, AiCodeEditor, AiErrorAlert, aiLoadErrorText, AiRunStatusChip, formatAiUsd, JsonBlock, parseJsonText,
    textError, toAiActionError, toJsonText,
} from './AiShared';
import AiRunDetailView, { RunLink } from './AiRunDetailView';

const BOUND_ARGS_HELP = 'Bound arguments are hidden from the model and always overwrite what it sends. Tokens: $endUserId, $subject.id, $subject.type, $input.<path>; anything else is a literal. Example: { "user_id": "$endUserId" }';

// ─── Tools (MCP links) ───────────────────────────────────────────────────────

function McpLinkDialog({ open, agentCode, servers, existing, linkedCodes, onClose, onSaved }: {
    open: boolean;
    agentCode: string;
    servers: AiMcpServerDto[];
    existing: AiAgentMcpLinkDto | null;
    linkedCodes: string[];
    onClose: () => void;
    onSaved: () => void;
}) {
    const [serverCode, setServerCode] = React.useState('');
    const [allowedTools, setAllowedTools] = React.useState<string[]>([]);
    const [boundArgs, setBoundArgs] = React.useState('');
    const [tools, setTools] = React.useState<AiMcpToolDto[] | null>(null);
    const [toolsLoading, setToolsLoading] = React.useState(false);
    const [toolsError, setToolsError] = React.useState<string | null>(null);
    const [error, setError] = React.useState<AiActionError | null>(null);
    const [saving, setSaving] = React.useState(false);

    React.useEffect(() => {
        if (!open) return;
        setServerCode(existing?.serverCode ?? '');
        setAllowedTools(existing?.allowedTools ?? []);
        setBoundArgs(toJsonText(existing?.boundArgs));
        setError(null);
    }, [open, existing]);

    React.useEffect(() => {
        if (!open || !serverCode) {
            setTools(null);
            return;
        }
        let cancelled = false;
        setToolsLoading(true);
        setToolsError(null);
        getAiMcpServerTools(serverCode).then(list => {
            if (cancelled) return;
            setToolsLoading(false);
            setTools(list);
            if (list == null) setToolsError(aiLoadErrorText(AI_LABELS.mcpTools, 'Could not list the server tools (you can still type tool names).'));
        });
        return () => { cancelled = true; };
    }, [open, serverCode]);

    const save = async () => {
        if (!serverCode) {
            setError(textError('Pick an MCP server.'));
            return;
        }
        const parsed = parseJsonText<AiBoundArgs>(boundArgs, 'bound args');
        if (parsed.error) {
            setError(textError(parsed.error));
            return;
        }
        setSaving(true);
        const result = await putAiAgentMcpLink(agentCode, serverCode, { allowedTools, boundArgs: parsed.value });
        setSaving(false);
        if (result.error != null) {
            setError(toAiActionError(result, 'Save failed'));
            return;
        }
        onSaved();
    };

    const toolNames = (tools ?? []).map(t => t.name);
    const toolByName = new Map((tools ?? []).map(t => [t.name, t] as [string, AiMcpToolDto]));
    const available = servers.filter(s => existing != null ? s.code === existing.serverCode : !linkedCodes.includes(s.code));

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{existing ? `Edit MCP link — ${existing.serverName || existing.serverCode}` : 'Link MCP server'}</DialogTitle>
            <DialogContent>
                <Stack gap={2} sx={{ mt: 1 }}>
                    <TextField select label="MCP server" value={serverCode} disabled={existing != null} fullWidth
                        onChange={e => { setServerCode(e.target.value); setAllowedTools([]); }}>
                        {available.map(s => (
                            <MenuItem key={s.code} value={s.code}>
                                {s.name} ({s.code}) · {s.dataClass}{s.status !== 'ACTIVE' ? ' · DISABLED' : ''}
                            </MenuItem>
                        ))}
                    </TextField>
                    {toolsError && <Alert severity="warning">{toolsError}</Alert>}
                    <Autocomplete
                        multiple
                        freeSolo
                        options={toolNames}
                        value={allowedTools}
                        loading={toolsLoading}
                        disableCloseOnSelect
                        onChange={(_, value) => setAllowedTools(value as string[])}
                        renderOption={(props, option) => {
                            const tool = toolByName.get(option);
                            return (
                                <li {...props} key={option}>
                                    <Stack>
                                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                            {option}{tool?.readOnly ? '' : tool ? '  (writes)' : ''}
                                        </Typography>
                                        {tool?.description && <Typography variant="caption" color="text.secondary">{tool.description}</Typography>}
                                    </Stack>
                                </li>
                            );
                        }}
                        renderInput={params => (
                            <TextField {...params} label="Allowed tools"
                                helperText={tools ? `${tools.length} tool(s) on the server right now — pick the ones this agent may call.` : 'Pick a server to load its live tools.'} />
                        )}
                    />
                    {tools && allowedTools.length < tools.length && (
                        <Box>
                            <Button size="small" onClick={() => setAllowedTools(toolNames)}>Select all {tools.length}</Button>
                        </Box>
                    )}
                    <AiCodeEditor label="Bound args (JSON object, optional)" language="json" path={`ai-agent-${agentCode}-bound-args.json`}
                        value={boundArgs} onChange={setBoundArgs} height={160} helper={BOUND_ARGS_HELP} />
                    <AiErrorAlert error={error} onClose={() => setError(null)} />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
            </DialogActions>
        </Dialog>
    );
}

function useAgentLinks(agentCode: string) {
    const [links, setLinks] = React.useState<AiAgentLinksDto | null>(null);
    const [loadError, setLoadError] = React.useState<string | null>(null);
    const [loading, setLoading] = React.useState(false);
    const reload = React.useCallback(async () => {
        setLoading(true);
        const l = await getAiAgentLinks(agentCode);
        setLoading(false);
        setLinks(l);
        setLoadError(l == null ? aiLoadErrorText(AI_LABELS.agentLinks, 'Failed to load links.') : null);
    }, [agentCode]);
    React.useEffect(() => { reload(); }, [reload]);
    return { links, loadError, loading, reload };
}

export function AgentToolsTab({ agentCode }: { agentCode: string }) {
    const { links, loadError, loading, reload } = useAgentLinks(agentCode);
    const [servers, setServers] = React.useState<AiMcpServerDto[]>([]);
    const [dialog, setDialog] = React.useState<{ open: boolean; existing: AiAgentMcpLinkDto | null }>({ open: false, existing: null });
    const [pendingDelete, setPendingDelete] = React.useState<AiAgentMcpLinkDto | null>(null);
    const [error, setError] = React.useState<AiActionError | null>(null);
    const [notice, setNotice] = React.useState<string | null>(null);

    React.useEffect(() => { getAiMcpServers().then(s => setServers(s ?? [])); }, []);

    const remove = async () => {
        if (pendingDelete == null) return;
        const target = pendingDelete;
        setPendingDelete(null);
        const result = await deleteAiAgentMcpLink(agentCode, target.serverCode);
        if (result.error != null) {
            setError(toAiActionError(result, 'Unlink failed'));
            return;
        }
        setNotice(`Unlinked ${target.serverCode}.`);
        reload();
    };

    const mcp = links?.mcpServers ?? [];
    return (
        <Stack gap={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" color="text.secondary">MCP servers this agent can use, which of their tools, and arguments bound by the server.</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialog({ open: true, existing: null })}>Link MCP server</Button>
            </Stack>
            {loadError && <Alert severity="error">{loadError}</Alert>}
            <AiErrorAlert error={error} onClose={() => setError(null)} />
            {notice && <Alert severity="success" onClose={() => setNotice(null)}>{notice}</Alert>}
            {loading && links == null ? <CircularProgress size={24} /> : (
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Server</TableCell>
                            <TableCell>Allowed tools</TableCell>
                            <TableCell>Bound args</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {mcp.length === 0 && (
                            <TableRow><TableCell colSpan={4}><Typography variant="body2" color="text.secondary">No MCP servers linked.</Typography></TableCell></TableRow>
                        )}
                        {mcp.map(link => (
                            <TableRow key={link.serverCode} sx={{ verticalAlign: 'top' }}>
                                <TableCell>
                                    <Typography variant="body2" fontWeight={600}>{link.serverName || link.serverCode}</Typography>
                                    <Typography variant="caption" color="text.secondary">{link.serverCode}</Typography>
                                </TableCell>
                                <TableCell>
                                    <Stack direction="row" gap={0.5} flexWrap="wrap">
                                        {link.allowedTools.length === 0 && <Typography variant="body2" color="text.secondary">none</Typography>}
                                        {link.allowedTools.map(t => <Chip key={t} size="small" label={t} sx={{ fontFamily: 'monospace' }} />)}
                                    </Stack>
                                </TableCell>
                                <TableCell sx={{ maxWidth: 360 }}>
                                    {link.boundArgs && Object.keys(link.boundArgs).length > 0
                                        ? <JsonBlock value={link.boundArgs} maxHeight={160} />
                                        : <Typography variant="body2" color="text.secondary">—</Typography>}
                                </TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                    <Tooltip title="Edit"><IconButton size="small" onClick={() => setDialog({ open: true, existing: link })}><EditIcon fontSize="small" /></IconButton></Tooltip>
                                    <Tooltip title="Unlink"><IconButton size="small" color="error" onClick={() => setPendingDelete(link)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
            <McpLinkDialog
                open={dialog.open}
                agentCode={agentCode}
                servers={servers}
                existing={dialog.existing}
                linkedCodes={mcp.map(m => m.serverCode)}
                onClose={() => setDialog({ open: false, existing: null })}
                onSaved={() => { setDialog({ open: false, existing: null }); setNotice('MCP link saved.'); reload(); }}
            />
            <ConfirmDialog
                open={pendingDelete != null}
                title="Unlink MCP server?"
                text={<>The agent will no longer see the tools of <b>{pendingDelete?.serverName || pendingDelete?.serverCode}</b>.</>}
                confirmLabel="Unlink"
                confirmColor="error"
                onClose={() => setPendingDelete(null)}
                onConfirm={remove}
            />
        </Stack>
    );
}

// ─── Delegation (subagents, tasks) ───────────────────────────────────────────

function LinkSection({ title, help, rows, options, kind, onSave, onRemove }: {
    title: string;
    help: string;
    rows: Array<{ code: string; maxCallsPerRun: number }>;
    options: string[];
    kind: 'agent' | 'task';
    onSave: (code: string, maxCalls: number) => Promise<boolean>;
    onRemove: (code: string) => void;
}) {
    const [code, setCode] = React.useState<string | null>(null);
    const [maxCalls, setMaxCalls] = React.useState('3');
    const [saving, setSaving] = React.useState(false);
    const submit = async () => {
        if (!code) return;
        setSaving(true);
        const ok = await onSave(code, Number(maxCalls) || 1);
        setSaving(false);
        if (ok) setCode(null);
    };
    return (
        <Card variant="outlined">
            <CardContent>
                <Typography variant="h6">{title}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{help}</Typography>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>{kind === 'agent' ? 'Subagent' : 'Task'}</TableCell>
                            <TableCell>Max calls per run</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.length === 0 && (
                            <TableRow><TableCell colSpan={3}><Typography variant="body2" color="text.secondary">None.</Typography></TableCell></TableRow>
                        )}
                        {rows.map(r => (
                            <TableRow key={r.code}>
                                <TableCell>
                                    <MuiLink component={RouterLink} to={`/ai/${kind === 'agent' ? 'agents' : 'tasks'}/${encodeURIComponent(r.code)}`}
                                        underline="hover" sx={{ fontFamily: 'monospace' }}>{r.code}</MuiLink>
                                </TableCell>
                                <TableCell>{r.maxCallsPerRun}</TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                    <Tooltip title="Edit"><IconButton size="small" onClick={() => { setCode(r.code); setMaxCalls(String(r.maxCallsPerRun)); }}><EditIcon fontSize="small" /></IconButton></Tooltip>
                                    <Tooltip title="Remove"><IconButton size="small" color="error" onClick={() => onRemove(r.code)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                <Stack direction="row" gap={1} alignItems="flex-start" sx={{ mt: 2 }} flexWrap="wrap">
                    <Autocomplete
                        freeSolo
                        options={options}
                        value={code}
                        onChange={(_, v) => setCode(v)}
                        onInputChange={(_, v) => setCode(v || null)}
                        sx={{ minWidth: 280, flex: 1 }}
                        renderInput={params => <TextField {...params} size="small" label={kind === 'agent' ? 'Subagent code' : 'Task code'} />}
                    />
                    <TextField size="small" label="Max calls per run" value={maxCalls} sx={{ width: 150 }}
                        onChange={e => setMaxCalls(e.target.value.replace(/[^\d]/g, ''))} />
                    <Button variant="contained" startIcon={<AddIcon />} disabled={!code || saving || !maxCalls} onClick={submit}>
                        {rows.some(r => r.code === code) ? 'Update' : 'Add'}
                    </Button>
                </Stack>
            </CardContent>
        </Card>
    );
}

export function AgentDelegationTab({ agentCode }: { agentCode: string }) {
    const { links, loadError, reload } = useAgentLinks(agentCode);
    const [agentOptions, setAgentOptions] = React.useState<string[]>([]);
    const [taskOptions, setTaskOptions] = React.useState<string[]>([]);
    const [error, setError] = React.useState<AiActionError | null>(null);
    const [notice, setNotice] = React.useState<string | null>(null);
    const [pendingDelete, setPendingDelete] = React.useState<{ kind: 'agent' | 'task'; code: string } | null>(null);

    React.useEffect(() => {
        getAiAgents({}, 0, 200).then(p => setAgentOptions((p?.items ?? []).map(a => a.code).filter(c => c !== agentCode)));
        getAiTasks({}, 0, 200).then(p => setTaskOptions((p?.items ?? []).map(t => t.code)));
    }, [agentCode]);

    const save = (kind: 'agent' | 'task') => async (code: string, maxCalls: number) => {
        setError(null);
        const result = kind === 'agent'
            ? await putAiAgentSubagentLink(agentCode, code, maxCalls)
            : await putAiAgentTaskLink(agentCode, code, maxCalls);
        if (result.error != null) {
            setError(toAiActionError(result, `Could not link ${kind} ${code}`));
            return false;
        }
        setNotice(`${kind === 'agent' ? 'Subagent' : 'Task'} ${code} linked (max ${maxCalls} calls per run).`);
        reload();
        return true;
    };

    const remove = async () => {
        if (pendingDelete == null) return;
        const { kind, code } = pendingDelete;
        setPendingDelete(null);
        const result = kind === 'agent' ? await deleteAiAgentSubagentLink(agentCode, code) : await deleteAiAgentTaskLink(agentCode, code);
        if (result.error != null) {
            setError(toAiActionError(result, `Could not remove ${kind} ${code}`));
            return;
        }
        setNotice(`Removed ${kind} ${code}.`);
        reload();
    };

    return (
        <Stack gap={2}>
            {loadError && <Alert severity="error">{loadError}</Alert>}
            <AiErrorAlert error={error} onClose={() => setError(null)} />
            {notice && <Alert severity="success" onClose={() => setNotice(null)}>{notice}</Alert>}
            <Grid container spacing={2}>
                <Grid item xs={12} lg={6}>
                    <LinkSection
                        title="Subagents"
                        help="Agents this agent may delegate to (each call starts a child run). Self-links and cycles are rejected (409)."
                        kind="agent"
                        rows={(links?.subagents ?? []).map(s => ({ code: s.agentCode, maxCallsPerRun: s.maxCallsPerRun }))}
                        options={agentOptions}
                        onSave={save('agent')}
                        onRemove={code => setPendingDelete({ kind: 'agent', code })}
                    />
                </Grid>
                <Grid item xs={12} lg={6}>
                    <LinkSection
                        title="Tasks"
                        help="Tasks this agent may call as tools."
                        kind="task"
                        rows={(links?.tasks ?? []).map(t => ({ code: t.taskCode, maxCallsPerRun: t.maxCallsPerRun }))}
                        options={taskOptions}
                        onSave={save('task')}
                        onRemove={code => setPendingDelete({ kind: 'task', code })}
                    />
                </Grid>
            </Grid>
            <ConfirmDialog
                open={pendingDelete != null}
                title={pendingDelete?.kind === 'agent' ? 'Remove subagent?' : 'Remove task?'}
                text={<>The agent will no longer be able to call <b>{pendingDelete?.code}</b>.</>}
                confirmLabel="Remove"
                confirmColor="error"
                onClose={() => setPendingDelete(null)}
                onConfirm={remove}
            />
        </Stack>
    );
}

// ─── Effective tools ─────────────────────────────────────────────────────────

export function AgentEffectiveToolsTab({ agentCode }: { agentCode: string }) {
    const [tools, setTools] = React.useState<AiAgentToolsDto | null>(null);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [expanded, setExpanded] = React.useState<string | null>(null);

    const load = React.useCallback(async () => {
        setLoading(true);
        const t = await getAiAgentTools(agentCode);
        setLoading(false);
        setTools(t);
        setError(t == null ? aiLoadErrorText(AI_LABELS.agentTools, 'Failed to load effective tools.') : null);
    }, [agentCode]);
    React.useEffect(() => { load(); }, [load]);

    return (
        <Stack gap={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" color="text.secondary">Exactly what the model will see: MCP tools (filtered), subagents and tasks as tools. Bound args are hidden from the model.</Typography>
                <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>Refresh</Button>
            </Stack>
            {error && <Alert severity="error">{error}</Alert>}
            {(tools?.warnings ?? []).map((w, i) => <Alert key={i} severity="warning">{w}</Alert>)}
            {loading && tools == null ? <CircularProgress size={24} /> : (
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Kind</TableCell>
                            <TableCell>Target</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell>Bound args</TableCell>
                            <TableCell>Input schema</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {(tools?.tools ?? []).length === 0 && (
                            <TableRow><TableCell colSpan={6}><Typography variant="body2" color="text.secondary">The model sees no tools.</Typography></TableCell></TableRow>
                        )}
                        {(tools?.tools ?? []).map(t => (
                            <React.Fragment key={t.name}>
                                <TableRow sx={{ verticalAlign: 'top' }}>
                                    <TableCell sx={{ fontFamily: 'monospace' }}>{t.name}</TableCell>
                                    <TableCell><Chip size="small" variant="outlined" color={t.kind === 'MCP' ? 'primary' : t.kind === 'SUBAGENT' ? 'secondary' : 'default'} label={t.kind} /></TableCell>
                                    <TableCell>{t.target}</TableCell>
                                    <TableCell sx={{ maxWidth: 420 }}><Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{t.description || '—'}</Typography></TableCell>
                                    <TableCell>
                                        {t.boundArgs && Object.keys(t.boundArgs).length > 0
                                            ? <Stack gap={0.5}>{Object.entries(t.boundArgs).map(([k, v]) => <Chip key={k} size="small" variant="outlined" label={`${k} = ${typeof v === 'string' ? v : JSON.stringify(v)}`} />)}</Stack>
                                            : '—'}
                                    </TableCell>
                                    <TableCell>
                                        {t.inputSchema
                                            ? <Button size="small" onClick={() => setExpanded(expanded === t.name ? null : t.name)}>{expanded === t.name ? 'Hide' : 'Show'}</Button>
                                            : '—'}
                                    </TableCell>
                                </TableRow>
                                {expanded === t.name && (
                                    <TableRow>
                                        <TableCell colSpan={6}><JsonBlock value={t.inputSchema} maxHeight={320} /></TableCell>
                                    </TableRow>
                                )}
                            </React.Fragment>
                        ))}
                    </TableBody>
                </Table>
            )}
        </Stack>
    );
}

// ─── Test run ────────────────────────────────────────────────────────────────

const POLL_MS = 1500;

export function AgentTestRunTab({ agentCode }: { agentCode: string }) {
    const [messages, setMessages] = React.useState<AiAgentMessageDto[]>([{ role: 'user', content: '' }]);
    const [inputText, setInputText] = React.useState('');
    const [endUserId, setEndUserId] = React.useState('');
    const [subjectType, setSubjectType] = React.useState('');
    const [subjectId, setSubjectId] = React.useState('');
    const [error, setError] = React.useState<AiActionError | null>(null);
    const [runId, setRunId] = React.useState<string | null>(null);
    const [run, setRun] = React.useState<AiRunDetailDto | null>(null);
    const [polling, setPolling] = React.useState(false);
    const [starting, setStarting] = React.useState(false);
    const timer = React.useRef<number | null>(null);

    const stopPolling = React.useCallback(() => {
        if (timer.current != null) window.clearTimeout(timer.current);
        timer.current = null;
        setPolling(false);
    }, []);

    React.useEffect(() => stopPolling, [stopPolling]);

    const poll = React.useCallback(async (id: string) => {
        const detail = await getAiRun(id);
        if (detail == null) {
            setError(textError(aiLoadErrorText(AI_LABELS.run, 'Failed to poll the run.')));
            stopPolling();
            return;
        }
        setRun(detail);
        if (detail.status === 'SUCCEEDED' || detail.status === 'FAILED') {
            stopPolling();
            return;
        }
        timer.current = window.setTimeout(() => poll(id), POLL_MS);
    }, [stopPolling]);

    const start = async () => {
        const input = parseJsonText<AiJsonObject>(inputText, 'input');
        if (input.error) {
            setError(textError(input.error));
            return;
        }
        const cleanMessages = messages.filter(m => m.content.trim() !== '');
        if (cleanMessages.length === 0 && input.value == null) {
            setError(textError('Add at least one message or an input object.'));
            return;
        }
        stopPolling();
        setError(null);
        setRun(null);
        setRunId(null);
        setStarting(true);
        const result = await startAiAgentRun(agentCode, {
            messages: cleanMessages,
            input: input.value,
            subject: subjectType.trim() ? { type: subjectType.trim(), id: subjectId.trim() } : null,
            endUserId: endUserId ? Number(endUserId) : null,
        }, 0);
        setStarting(false);
        if (result.error != null || result.data == null) {
            setError(toAiActionError(result, 'Could not start the run') ?? textError('Empty response'));
            return;
        }
        setRunId(result.data.runId);
        setPolling(true);
        poll(result.data.runId);
    };

    const updateMessage = (index: number, p: Partial<AiAgentMessageDto>) =>
        setMessages(list => list.map((m, i) => (i === index ? { ...m, ...p } : m)));

    return (
        <Stack gap={2}>
            <Card variant="outlined">
                <CardContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12} lg={7}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>Messages</Typography>
                            <Stack gap={1.5}>
                                {messages.map((m, i) => (
                                    <Stack key={i} direction="row" gap={1} alignItems="flex-start">
                                        <TextField select size="small" value={m.role} sx={{ width: 130 }}
                                            onChange={e => updateMessage(i, { role: e.target.value as AiAgentMessageDto['role'] })}>
                                            <MenuItem value="user">user</MenuItem>
                                            <MenuItem value="assistant">assistant</MenuItem>
                                        </TextField>
                                        <TextField multiline minRows={2} maxRows={10} fullWidth size="small" value={m.content}
                                            placeholder={m.role === 'user' ? 'Hi, my bet was not settled' : 'Assistant reply'}
                                            onChange={e => updateMessage(i, { content: e.target.value })} />
                                        <IconButton size="small" disabled={messages.length <= 1}
                                            onClick={() => setMessages(list => list.filter((_, j) => j !== i))}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Stack>
                                ))}
                                <Box>
                                    <Button size="small" startIcon={<AddIcon />} onClick={() => setMessages(list => [
                                        ...list, { role: list.length > 0 && list[list.length - 1].role === 'user' ? 'assistant' : 'user', content: '' },
                                    ])}>Add message</Button>
                                </Box>
                            </Stack>
                        </Grid>
                        <Grid item xs={12} lg={5}>
                            <Stack gap={2}>
                                <AiCodeEditor label="Input (JSON object, optional)" language="json" path={`ai-agent-${agentCode}-run-input.json`}
                                    value={inputText} onChange={setInputText} height={140}
                                    helper="Used as the first user message (JSON) when messages are empty; also feeds $input.<path> bound args." />
                                <TextField size="small" label="End user id (optional)" value={endUserId}
                                    onChange={e => setEndUserId(e.target.value.replace(/\D/g, ''))} />
                                <Stack direction="row" gap={1}>
                                    <TextField size="small" label="Subject type" value={subjectType} onChange={e => setSubjectType(e.target.value)} placeholder="support_chat" />
                                    <TextField size="small" label="Subject id" value={subjectId} onChange={e => setSubjectId(e.target.value)} />
                                </Stack>
                                <Stack direction="row" gap={1}>
                                    <Button variant="contained" startIcon={starting ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
                                        disabled={starting || polling} onClick={start}>Start run</Button>
                                    {polling && <Button startIcon={<StopIcon />} onClick={stopPolling}>Stop polling</Button>}
                                </Stack>
                                <Typography variant="caption" color="text.secondary">
                                    Real run (caller <code>betup-ai-admin</code>): tools are really called and cost counts toward budgets.
                                </Typography>
                            </Stack>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
            <AiErrorAlert error={error} onClose={() => setError(null)} />
            {runId && (
                <Card variant="outlined">
                    <CardContent>
                        <Stack direction="row" gap={1.5} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap">
                            <Typography variant="h6">Run</Typography>
                            <RunLink runId={runId} short={false} />
                            <AiRunStatusChip status={run?.status ?? 'QUEUED'} />
                            {polling && <CircularProgress size={18} />}
                            {run && <Typography variant="body2" color="text.secondary">{run.turns} turn(s) · {formatAiUsd(run.costUsd)}</Typography>}
                        </Stack>
                        {run && <AiRunDetailView run={run} />}
                    </CardContent>
                </Card>
            )}
        </Stack>
    );
}
